import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Settings, LogOut } from 'lucide-react';
import useAuthStore from '../../../stores/authStore';
import useOrderStore from '../../../stores/orderStore';
import useUIStore from '../../../stores/uiStore';
import useDashboardStore from '../../../stores/dashboardStore';
import { useAdCampaigns } from '../../../../presentation/hooks/marketing/useAdCampaigns';
import { splitCampaigns, todayISO } from '../../../../presentation/dashboard/marketing/adCampaignMapping';
import styles from './SideNavigation.module.css';

/* Figma 15:6425 사이드바 아이콘 — public/images/sidenav/v2/icons/<name>.svg
   CSS mask로 currentColor 재색상(활성/비활성/hover 대응) */
const ICON_BASE = '/images/sidenav/v2/icons';
const maskStyle = (name, size) => ({
  width: size,
  height: size,
  WebkitMaskImage: `url(${ICON_BASE}/${name}.svg)`,
  maskImage: `url(${ICON_BASE}/${name}.svg)`,
});

const MaskIcon = ({ name, size = 20, className }) => (
  <span
    aria-hidden
    className={`${styles.maskIcon}${className ? ` ${className}` : ''}`}
    style={maskStyle(name, size)}
  />
);

/* ─── 메뉴 구조 (icon = Figma SVG 파일명) ─── */
const SECTIONS = [
  {
    title: '운영',
    items: [
      { id: 'home', path: '/dashboard/home', label: '홈', icon: 'home' },
      {
        id: 'rx',
        path: '/dashboard/rx',
        label: '처방전 관리',
        icon: 'rx',
        badgeKey: 'rx',
      },
      {
        id: 'patient',
        path: '/dashboard/patient',
        label: '환자관리',
        icon: 'patient',
      },
      {
        id: 'adherence',
        path: '/dashboard/adherence',
        label: '복약 이행 관리',
        icon: 'adherence',
      },
    ],
  },
  {
    title: '마케팅',
    items: [
      {
        id: 'events',
        label: '이벤트',
        icon: 'events',
        accordion: true,
        children: [
          {
            id: 'event-create',
            label: '이벤트 만들기',
            path: '/dashboard/coupons/distribute',
          },
          {
            id: 'event-distributions',
            label: '지급내역',
            path: '/dashboard/events/distributions',
          },
          {
            id: 'event-history',
            label: '사용내역',
            path: '/dashboard/events/history',
          },
        ],
      },
      {
        id: 'external-promotion',
        label: '외부 프로모션',
        icon: 'coupon',
        accordion: true,
        tag: 'NEW',
        showChevron: false,
        children: [
          {
            id: 'promotion-active',
            label: '진행 중',
            path: '/dashboard/marketing',
          },
          {
            id: 'promotion-review',
            label: '신규 캠페인 검토',
            path: '/dashboard/marketing?view=review',
          },
          {
            id: 'promotion-done',
            label: '종료',
            path: '/dashboard/marketing?view=done',
          },
          {
            id: 'promotion-history',
            label: '사용내역',
            path: '/dashboard/marketing?view=history',
          },
        ],
      },
      {
        id: 'points',
        label: '포인트',
        icon: 'point',
        accordion: true,
        showChevron: false,
        children: [
          {
            id: 'point-status',
            label: '팜포인트 현황',
            path: '/dashboard/points/status',
          },
          {
            id: 'point-rules',
            label: '팜 포인트 적립 기준',
            path: '/dashboard/points/rules',
          },
          {
            id: 'point-paypoint',
            label: '팜페이포인트 현황',
            path: '/dashboard/points/paypoint',
          },
        ],
      },
    ],
  },
  {
    title: '관리',
    items: [
      {
        id: 'insight',
        path: '/dashboard/insight',
        label: '판매 인사이트',
        icon: 'insight',
        accordion: true,
        showChevron: false,
        children: [
          {
            id: 'insight-overview',
            label: '전체 현황',
            path: '/dashboard/insight',
          },
          // 매출 현황 메뉴 숨김 (디자인 QA 10/23)
          {
            id: 'insight-health-heading',
            label: '건기식 판매 인사이트',
            heading: true,
          },
          {
            id: 'insight-national',
            label: '전국 TOP',
            path: '/dashboard/insight?view=national',
          },
          {
            id: 'insight-region',
            label: '우리 지역 TOP',
            path: '/dashboard/insight?view=region',
          },
          {
            id: 'insight-pharmacy',
            label: '약국취급률 TOP',
            path: '/dashboard/insight?view=pharmacy',
          },
          // 질병별·연령성별·외국인 TOP 은 POS 에 고객 정보가 없어 제외 (10/15 결정)
        ],
      },
      {
        id: 'settlement',
        path: '/dashboard/settlement/home',
        label: '정산',
        icon: 'settlement',
      },
    ],
  },
];

const eventPrefixes = [
  '/dashboard/events',
  '/dashboard/coupons',
  '/dashboard/points/distribute',
  '/dashboard/points/distributions',
  '/dashboard/points/history',
];

const pointPrefixes = [
  '/dashboard/points/status',
  '/dashboard/points/rules',
  '/dashboard/points/paypoint',
];

const isActiveItem = (item, currentPath) => {
  if (item.id === 'points') return pointPrefixes.includes(currentPath);
  if (item.accordion && item.id === 'events') {
    return eventPrefixes.some(p => currentPath.startsWith(p));
  }
  if (item.id === 'external-promotion')
    return currentPath === '/dashboard/marketing';
  return currentPath === item.path;
};

const SideNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const pharmacy = useAuthStore(state => state.pharmacy);
  const logout = useAuthStore(state => state.logout);

  const orders = useOrderStore(state => state.orders);
  const rxBadgeCount = orders.filter(
    o =>
      o.status === 'pending' || o.status === 'paid' || o.status === 'preparing'
  ).length;

  /* 외부 프로모션 개수 — 외부 프로모션 화면과 같은 쿼리(캐시 공유) */
  const { data: adCampaigns } = useAdCampaigns(pharmacy?.business_registration_number);
  const promo = adCampaigns ? splitCampaigns(adCampaigns.items, todayISO()) : null;
  const promoCounts = promo && {
    'promotion-active': { count: promo.active.length },
    'promotion-review': { count: promo.pendingReview, alert: promo.pendingReview > 0 },
  };

  /* 오늘 현황 KPI — 제목 오른쪽 ▲/▼ 로 접기·펼치기 (Figma 15:4701 · 409:14532, 디자인 QA 10/23) */
  const [todayOpen, setTodayOpen] = useState(true);
  const visitKpi = useDashboardStore(state => state.visitKpi);
  const rxKpi = useDashboardStore(state => state.rxKpi);
  const couponKpi = useDashboardStore(state => state.couponKpi);
  const pointKpi = useDashboardStore(state => state.pointKpi);

  /* Collapsed state — 자동 접기는 비활성화, 사용자 수동 토글만 동작 */
  const isSidebarCollapsed = useUIStore(state => state.isSidebarCollapsed);
  const setSidebarCollapsed = useUIStore(state => state.setSidebarCollapsed);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(!useUIStore.getState().isSidebarCollapsed);
  }, [setSidebarCollapsed]);

  const expandIfCollapsed = useCallback(() => {
    if (useUIStore.getState().isSidebarCollapsed) {
      setSidebarCollapsed(false);
    }
  }, [setSidebarCollapsed]);

  /* 아코디언 상태 */
  const [expanded, setExpanded] = useState(() => {
    const init = {};
    if (eventPrefixes.some(p => currentPath.startsWith(p))) {
      init['events'] = true;
    }
    if (currentPath === '/dashboard/marketing')
      init['external-promotion'] = true;
    if (pointPrefixes.includes(currentPath)) init['points'] = true;
    if (currentPath === '/dashboard/insight') init['insight'] = true;
    return init;
  });
  const toggleAccordion = id =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  /* 프로필 드롭다운 */
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = e => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pharmacyName = pharmacy?.name || '행복약국';
  const pharmacistName =
    pharmacy?.representative_name || pharmacy?.pharmacist_name || '약사';

  const handleItemClick = item => {
    if (item.accordion) {
      if (isSidebarCollapsed) {
        expandIfCollapsed();
        setExpanded(prev => ({ ...prev, [item.id]: true }));
        navigate(item.children[0].path);
      } else {
        toggleAccordion(item.id);
      }
      return;
    }
    expandIfCollapsed();
    navigate(item.path);
  };

  const renderItem = item => {
    const active = isActiveItem(item, currentPath);
    const isOpen = !!expanded[item.id];
    const showBadge = item.badgeKey === 'rx' && rxBadgeCount > 0;

    return (
      <div
        key={item.id}
        className={item.accordion ? styles.accordionGroup : undefined}
      >
        <button
          className={`${styles.navItem} ${active ? styles.active : ''}`}
          onClick={() => handleItemClick(item)}
          aria-label={item.label}
        >
          <MaskIcon name={item.icon} size={20} />
          <span className={styles.navLabel}>{item.label}</span>
          {showBadge && <span className={styles.navBadge}>{rxBadgeCount}</span>}
          {item.tag && <span className={styles.navTag}>{item.tag}</span>}
          {item.accordion && item.showChevron !== false && (
            <span className={styles.chevron}>
              {isOpen ? (
                <ChevronDown size={16} strokeWidth={1.5} />
              ) : (
                <ChevronRight size={16} strokeWidth={1.5} />
              )}
            </span>
          )}
        </button>
        {item.accordion && (
          <div
            className={`${styles.subMenu} ${!isSidebarCollapsed && isOpen ? styles.subMenuOpen : ''}`}
          >
            {item.children.map(child =>
              child.heading ? (
                <div className={styles.subHeading} key={child.id}>
                  {child.label}
                </div>
              ) : (
                <button
                  key={child.id}
                  className={`${styles.subItem} ${`${currentPath}${location.search}` === child.path || (!location.search && currentPath === child.path) ? styles.subItemActive : ''}`}
                  onClick={() => navigate(child.path)}
                >
                  <span>{child.label}</span>
                  {(child.count ?? promoCounts?.[child.id]?.count) != null && (
                    <b className={(child.alert ?? promoCounts?.[child.id]?.alert) ? styles.subCountAlert : ''}>
                      {child.count ?? promoCounts[child.id].count}
                    </b>
                  )}
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav
      className={`${styles.sidebar} ${isSidebarCollapsed ? styles.sidebarCollapsed : ''}`}
    >
      {/* 헤더 — Figma: 펼침=로고(좌)+사이드바 닫기 아이콘(우), 접힘=사이드바 열기 아이콘 (튀어나온 탭 제거) */}
      <div className={styles.header}>
        {isSidebarCollapsed ? (
          <button
            className={styles.toggleBtn}
            onClick={toggleSidebar}
            aria-label="사이드바 확장"
          >
            <MaskIcon name="sidebar-open" size={20} />
          </button>
        ) : (
          <>
            <img
              src="/images/logo-pharmview-v2.png"
              alt="팜인사이트"
              className={styles.logoFull}
            />
            <button
              className={styles.toggleBtn}
              onClick={toggleSidebar}
              aria-label="사이드바 접기"
            >
              <MaskIcon name="sidebar-close" size={20} />
            </button>
          </>
        )}
      </div>

      {/* 메뉴 영역 */}
      <div className={styles.nav}>
        {SECTIONS.map((section, idx) => (
          <div key={section.title} className={styles.section}>
            {idx > 0 && isSidebarCollapsed && (
              <div className={styles.collapsedDivider} />
            )}
            <div className={styles.sectionTitle}>{section.title}</div>
            {section.items.map(renderItem)}
          </div>
        ))}
      </div>

      {/* 오늘 현황 (확장 시에만, 프로필 위) */}
      {!isSidebarCollapsed && (
        <div
          className={`${styles.trendSection} ${currentPath === '/dashboard/insight' ? styles.trendSectionCompact : ''}`}
        >
          <div className={styles.sectionTitle}>
            <span>오늘 현황</span>
            <button
              type="button"
              className={styles.sectionToggle}
              aria-expanded={todayOpen}
              aria-label={todayOpen ? '오늘 현황 접기' : '오늘 현황 펼치기'}
              onClick={() => setTodayOpen(v => !v)}
            >
              <img src="/images/marketing/icon-down.svg" width="22" height="22" alt="" style={todayOpen ? { transform: 'rotate(180deg)' } : undefined} />
            </button>
          </div>
          {todayOpen && <div className={styles.trendGrid}>
            <TrendCard
              label="앱 방문"
              value={visitKpi?.app_visit?.today}
              unit="명"
              delta={visitKpi?.app_visit_delta_pct}
            />
            <TrendCard
              label="처방전"
              value={rxKpi?.today_total}
              unit="건"
              delta={rxKpi?.yesterday_delta_pct}
            />
            <TrendCard
              label="쿠폰"
              value={couponKpi?.total_issued}
              unit="개"
              delta={couponKpi?.today_vs_yesterday?.percent}
            />
            <TrendCard
              label="처방전"
              value={pointKpi?.today_used}
              unit="P"
              delta={pointKpi?.today_vs_yesterday?.percent}
            />
          </div>}
        </div>
      )}

      {/* 프로필 영역 */}
      <div className={styles.profileWrap} ref={profileRef}>
        <div className={styles.profile}>
          <button
            className={styles.profileMain}
            onClick={() => setProfileOpen(prev => !prev)}
          >
            <div className={styles.avatar}>{pharmacyName.charAt(0)}</div>
            {!isSidebarCollapsed && (
              <div className={styles.profileInfo}>
                <div className={styles.profileName}>{pharmacyName}</div>
                <div className={styles.profileRole}>{pharmacistName}</div>
              </div>
            )}
          </button>
          {!isSidebarCollapsed && (
            <button
              className={styles.profileSettings}
              onClick={() => navigate('/dashboard/settings')}
              aria-label="약국 설정"
            >
              <MaskIcon name="cog" size={22} />
            </button>
          )}
        </div>

        {profileOpen && (
          <div
            className={`${styles.profileDropdown} ${isSidebarCollapsed ? styles.profileDropdownCollapsed : ''}`}
          >
            <button
              className={styles.profileDropdownItem}
              onClick={() => {
                navigate('/dashboard/settings');
                setProfileOpen(false);
              }}
            >
              <Settings size={16} strokeWidth={1.5} />
              <span>약국 설정</span>
            </button>
            <div className={styles.profileDropdownDivider} />
            <button
              className={styles.profileDropdownItem}
              onClick={() => {
                logout();
                navigate('/login');
              }}
            >
              <LogOut size={16} strokeWidth={1.5} />
              <span>로그아웃</span>
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

const TrendCard = ({ label, value, unit, delta }) => {
  const hasDelta =
    delta != null && !Number.isNaN(Number(delta)) && Number(delta) !== 0;
  const isUp = Number(delta) > 0;
  const display =
    value != null ? `${Number(value).toLocaleString()}${unit || ''}` : '-';

  return (
    <div className={styles.trendCard}>
      <div className={styles.trendCardTop}>
        <span className={styles.trendCardLabel}>{label}</span>
        {hasDelta && (
          <span
            className={`${styles.trendDelta} ${isUp ? styles.deltaUp : styles.deltaDown}`}
          >
            <MaskIcon name={isUp ? 'trend-up' : 'trend-down'} size={14} />
            {Math.abs(Number(delta))}%
          </span>
        )}
      </div>
      <div className={styles.trendCardValue}>{display}</div>
    </div>
  );
};

export default SideNavigation;
